// ---------------------------------------------------------------------------
// Telling the cashier where they stand.
//
// ONE BADGE. The header used to carry a second chip of its own, driven by
// `navigator.onLine`. That reports whether the machine has a network interface
// up, which on a shop's Wi-Fi is very often true while nothing can actually
// reach the server - so the two badges regularly disagreed, and the reassuring
// one was the one that was wrong. There is now exactly one, and it asks the
// server.
//
// WHAT THE ONE WORD MEANS. It is about the POS BACKEND and nothing else:
//
//   Online       the till reached the server on its last probe;
//   Offline      it did not, or the machine has no network at all;
//   Syncing      it is handing queued sales over right now;
//   Attention    something needs a person - a sale the server refused.
//
// A DISCONNECTED PRINTER DOES NOT CHANGE IT. A shop whose kitchen printer is
// unplugged is still selling: the money is being recorded, the receipts can be
// printed from the browser, and telling the cashier they are "offline" would be
// false. The printer's state is a detail, and lives in the details.
//
// The details behind the badge - the browser's own opinion, the server, both
// printers, what is waiting, what was refused, when the last sync worked - are
// too much for a header and are one press away instead.
// ---------------------------------------------------------------------------

import { useCallback, useId, useRef, useState } from 'react';
import { CloudOff, Printer, RefreshCw, TriangleAlert, Wifi } from 'lucide-react';
import clsx from 'clsx';
import { Link } from 'react-router-dom';
import { useI18n } from '../../i18n/I18nProvider';
import { isEffectivelyOffline, queueSummary, useOfflineStore } from '../../stores/offlineStore';
import { syncNow } from '../../lib/syncEngine';
import { usePrinterDevices } from '../../api/printerDevices.api';
import { HeaderPopover } from './HeaderPopover';

/// What the one word says. Ordered by how much it wants a person.
type MainState = 'offline' | 'attention' | 'syncing' | 'online';

export function ConnectionStatus() {
  const { t, locale } = useI18n();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const labelId = useId();
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);

  const browserOnline = useOfflineStore((state) => state.browserOnline);
  const serverReachable = useOfflineStore((state) => state.serverReachable);
  const phase = useOfflineStore((state) => state.phase);
  const pending = useOfflineStore((state) => state.pending);
  const lastSync = useOfflineStore((state) => state.lastSync);

  const offline = isEffectivelyOffline({ browserOnline, serverReachable });
  const { total, failed } = queueSummary(pending);
  const syncing = phase === 'syncing';

  // Read only while the panel is open. The printers are a detail nobody is
  // looking at with it closed, and a till should not poll them all day to
  // answer a question that has not been asked.
  const { data: printers } = usePrinterDevices();

  /// The badge's own state. Note what is NOT in it: the printers.
  const state: MainState = offline
    ? 'offline'
    : failed > 0
      ? 'attention'
      : syncing
        ? 'syncing'
        : 'online';

  const word =
    state === 'offline'
      ? t.offline.offline
      : state === 'attention'
        ? t.connectivity.attention
        : state === 'syncing'
          ? t.connectivity.syncing
          : t.offline.online;

  const tone = state === 'offline' || state === 'attention' ? 'warn' : 'calm';

  // EVERY PRINTER THE SHOP OWNS, by its own name.
  //
  // This used to be two fixed rows - "cashier printer" and "kitchen printer" -
  // read from a role-keyed endpoint. A butcher with a grill bench and a meat
  // bench saw one of them and no hint that the other existed, and the header
  // is exactly where somebody looks when a ticket has not appeared.
  //
  // Guarded because the header is the last place in the application that may
  // white-screen: a half-written response or an old service worker can produce
  // something that is not an array.
  const configuredPrinters = Array.isArray(printers) ? printers : [];

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        id={labelId}
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
        aria-haspopup="dialog"
        className={clsx(
          'flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-control border px-2.5 py-1.5 text-xs font-medium transition-colors',
          tone === 'warn'
            ? 'border-antique-gold/50 bg-antique-gold/10 text-antique-gold-deep hover:bg-antique-gold/20'
            : 'border-line bg-surface-soft text-ink-muted hover:bg-surface-sunken',
        )}
        // Announced politely rather than interrupting: this changes while a
        // cashier is mid-sale and must never steal focus from the keypad.
        role="status"
        aria-live="polite"
        title={offline ? t.offline.offlineDetail : t.connectivity.openDetails}
      >
        {state === 'offline' ? (
          <CloudOff className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        ) : state === 'syncing' ? (
          <RefreshCw className="h-3.5 w-3.5 shrink-0 animate-spin" aria-hidden="true" />
        ) : state === 'attention' ? (
          <TriangleAlert className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        ) : (
          <Wifi className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        )}

        {/* The word itself, always - "no badge" and "a badge I have not
            noticed" look identical to somebody who is not looking for it. */}
        <span className="hidden sm:inline">{word}</span>

        {/* How many sales the server has not been told about yet. Beside the
            word rather than in a badge of its own, because they are one fact:
            "offline, and three sales are waiting" is the whole sentence. */}
        {total > 0 ? (
          <span
            className={clsx(
              'numeric flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-bold',
              failed > 0 ? 'bg-burgundy-red text-white' : 'bg-antique-gold text-charcoal',
            )}
          >
            {total > 99 ? '99+' : total}
          </span>
        ) : null}
      </button>

      <HeaderPopover
        id="connectivity"
        open={open}
        onClose={close}
        anchorRef={buttonRef}
        labelledBy={labelId}
        width="w-[min(20rem,calc(100vw-1rem))]"
      >
        <div className="border-b border-line px-4 py-3">
          <p className="text-sm font-semibold text-ink">{t.connectivity.title}</p>
          <p className="mt-0.5 text-xs text-ink-muted">
            {offline ? t.offline.offlineDetail : t.connectivity.serverFine}
          </p>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3">
          <dl className="space-y-2 text-xs">
            {/* The two that decide the badge, first and named plainly. */}
            <Detail
              label={t.connectivity.backend}
              value={serverReachable === false ? t.connectivity.unreachable : serverReachable === null ? t.connectivity.checking : t.connectivity.reachable}
              tone={serverReachable === false ? 'bad' : serverReachable === null ? 'muted' : 'good'}
            />
            <Detail
              label={t.connectivity.device}
              value={browserOnline ? t.connectivity.hasNetwork : t.connectivity.noNetwork}
              tone={browserOnline ? 'good' : 'bad'}
            />

            {/* And the printers, which deliberately do not feed the badge -
                a printer that cannot be reached is not the till being
                offline, and one word cannot say both. */}
            {configuredPrinters.map((printer) => (
              <PrinterDetail key={printer.id} label={printer.name} printer={printer} t={t} />
            ))}

            <div className="!mt-3 border-t border-line pt-2.5" />

            <Detail
              label={t.connectivity.pending}
              value={String(total)}
              tone={total > 0 ? 'muted' : 'good'}
            />
            <Detail
              label={t.connectivity.failed}
              value={String(failed)}
              tone={failed > 0 ? 'bad' : 'good'}
            />
            <Detail
              label={t.connectivity.lastSync}
              value={
                lastSync
                  ? new Date(lastSync.at).toLocaleTimeString(locale === 'ar' ? 'ar-LB' : 'en-GB', {
                      timeZone: 'Asia/Beirut',
                      hour: '2-digit',
                      minute: '2-digit',
                    })
                  : t.connectivity.never
              }
              tone="muted"
            />
          </dl>
        </div>

        {total > 0 ? (
          <div className="flex items-center justify-between gap-2 border-t border-line px-4 py-2.5">
            <Link
              to="/offline-queue"
              onClick={close}
              className="text-xs underline underline-offset-2 hover:no-underline"
            >
              {t.offline.openQueue}
            </Link>

            {/* A manual push, for the cashier who can see the Wi-Fi is back
                and does not want to wait for the sweep. */}
            {!offline && !syncing ? (
              <button
                type="button"
                onClick={() => void syncNow({ manual: true })}
                className="btn-secondary min-h-8 gap-1.5 py-1 text-xs"
              >
                <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
                {t.offline.syncNow}
              </button>
            ) : null}
          </div>
        ) : null}
      </HeaderPopover>
    </>
  );
}

function Detail({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone: 'good' | 'bad' | 'muted';
}) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-ink-muted">{label}</dt>
      <dd
        className={clsx(
          'text-end font-semibold',
          tone === 'good' && 'text-butcher-green',
          tone === 'bad' && 'text-burgundy-red',
          tone === 'muted' && 'text-ink',
        )}
      >
        {value}
      </dd>
    </div>
  );
}

/// A printer's line.
///
/// "Not set up" is a different thing from "disconnected" and says so: a shop
/// that has never typed an address has nothing to fix, and telling it there is
/// a fault would send somebody looking for a cable that was never there.
function PrinterDetail({
  label,
  printer,
  t,
}: {
  label: string;
  printer: {
    enabled: boolean;
    connection: string;
    printerName: string | null;
    ipAddress: string | null;
    lastConnectionSuccessfulAt: string | null;
    lastConnectionError: string | null;
  } | null;
  t: ReturnType<typeof useI18n>['t'];
}) {
  // BOTH TRANSPORTS COUNT. This asked only for an address, so a printer on a
  // USB cable - which has a queue name and no address - read as "not set up"
  // while it was printing every receipt in the shop.
  const configured = Boolean(
    printer?.enabled &&
      (printer.connection === 'USB_WINDOWS'
        ? Boolean(printer.printerName?.trim())
        : Boolean(printer.ipAddress)),
  );
  const failing = configured && Boolean(printer?.lastConnectionError);

  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="flex items-center gap-1.5 text-ink-muted">
        <Printer className="h-3 w-3 shrink-0" aria-hidden="true" />
        {label}
      </dt>
      <dd
        className={clsx(
          'text-end font-semibold',
          !configured && 'text-ink-muted',
          configured && failing && 'text-burgundy-red',
          configured && !failing && 'text-butcher-green',
        )}
      >
        {!configured
          ? t.connectivity.printerNotSetUp
          : failing
            ? t.connectivity.printerDisconnected
            : t.connectivity.printerConnected}
      </dd>
    </div>
  );
}
